import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  of,
  tap,
  map,
  switchMap,
  from,
} from 'rxjs';
import { BusinessUnitService as ApiBusinessUnitService } from '@app/api/user-service/api/businessUnit.service';
import { UserBusinessUnitsService } from '@app/api/user-service/api/userBusinessUnits.service';
import { BusinessUnitTreeNode } from '@app/api/user-service/model/businessUnitTreeNode';
import { PageInfoKeycloakUserResponse } from '@app/api/user-service/model/pageInfoKeycloakUserResponse';
import { KeycloakUserResponse } from '@app/api/user-service/model/keycloakUserResponse';

@Injectable({
  providedIn: 'root',
})
export class BusinessUnitManagementService {
  private readonly apiBusinessUnitService = inject(ApiBusinessUnitService);
  private readonly userBusinessUnitsService = inject(UserBusinessUnitsService);

  private readonly treeSubject = new BehaviorSubject<BusinessUnitTreeNode[]>(
    [],
  );
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);

  readonly tree$ = this.treeSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();

  loadBusinessUnitTree(): Observable<BusinessUnitTreeNode[]> {
    this.loadingSubject.next(true);

    return this.apiBusinessUnitService
      .getBusinessUnitTree('body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        switchMap((response) => this.normalizeTreeResponse(response)),
        tap((tree) => {
          this.treeSubject.next(tree ?? []);
        }),
        catchError((error) => {
          console.error('Error loading business unit tree:', error);
          this.treeSubject.next([]);
          return of([]);
        }),
        finalize(() => {
          this.loadingSubject.next(false);
        }),
      );
  }

  private normalizeTreeResponse(
    response: BusinessUnitTreeNode[] | Blob | null,
  ): Observable<BusinessUnitTreeNode[]> {
    if (!response) {
      return of([]);
    }

    if (!(response instanceof Blob)) {
      return of(response);
    }

    return from(response.text()).pipe(
      map((text) => {
        try {
          return JSON.parse(text) as BusinessUnitTreeNode[];
        } catch (error) {
          console.error(
            '[BusinessUnitManagementService] failed to parse tree blob',
            error,
          );
          return [];
        }
      }),
    );
  }

  getUsersByUnitId(
    unitId: string,
    page: number = 0,
    size: number = 20,
    search?: string,
  ): Observable<Array<Record<string, unknown>>> {
    if (!unitId) {
      return of([]);
    }

    const businessUnitId = this.parseUnitId(unitId);
    if (isNaN(businessUnitId)) {
      console.error(
        '[BusinessUnitManagementService] Invalid business unit ID:',
        unitId,
      );
      return of([]);
    }

    const searchParam = search?.trim() || undefined;

    return this.userBusinessUnitsService
      .getUsersByBusinessUnit(
        businessUnitId,
        page,
        size,
        searchParam,
        'body',
        false,
        {
          httpHeaderAccept: 'application/json' as any,
        },
      )
      .pipe(
        switchMap((response) => this.normalizeUsersResponse(response)),
        map((pageInfo) => {
          const users = pageInfo?.pageContent || [];
          return this.mapUsersToTableFormat(users);
        }),
        catchError((error) => {
          console.error(
            '[BusinessUnitManagementService] Error loading users by business unit:',
            error,
          );
          return of([]);
        }),
      );
  }

  private parseUnitId(unitId: string): number {
    const parsed = Number.parseInt(unitId, 10);
    return Number.isNaN(parsed) ? NaN : parsed;
  }

  private normalizeUsersResponse(
    response: PageInfoKeycloakUserResponse | Blob | null,
  ): Observable<PageInfoKeycloakUserResponse> {
    if (!response) {
      return of({
        pageContent: [],
        totalElements: 0,
      } as PageInfoKeycloakUserResponse);
    }

    if (!(response instanceof Blob)) {
      return of(response);
    }

    return from(response.text()).pipe(
      map((text) => {
        try {
          const parsed = JSON.parse(text) as PageInfoKeycloakUserResponse;
          return parsed;
        } catch (error) {
          console.error(
            '[BusinessUnitManagementService] failed to parse getUsersByBusinessUnit blob',
            error,
          );
          return {
            pageContent: [],
            totalElements: 0,
          } as PageInfoKeycloakUserResponse;
        }
      }),
    );
  }

  private mapUsersToTableFormat(
    users: KeycloakUserResponse[],
  ): Array<Record<string, unknown>> {
    return users.map((user) => {
      const fullName = [user.firstName, user.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      const name = fullName || user.username || '--';

      // Get roles from realmRoles (Set<string>)
      const roles = user.realmRoles
        ? Array.from(user.realmRoles).join(', ')
        : '--';

      // Map enabled status to display text
      const status = user.enabled ? 'Active' : 'Inactive';

      return {
        name,
        username: user.username || '--',
        email: user.email || '--',
        role: roles,
        status,
        // Keep original user data for reference
        _original: user,
      };
    });
  }
}
